import { authPlugin, WuiFetchStrategy, AuthInitOptions, DialogPlugin, bootstrapStyles } from '@wui/common-library'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

import { clearBootThemeClass, useColorTheme } from '@/features/color-theme'

import { useAppBadge } from '@/shared/composables/browser-api'
import { useLocalization } from '@/shared/composables/localization'
import { tauriMode, appBaseUrl, isElectron, ensureAppBaseUrlReady } from '@/shared/utils/import-meta'
import { showFatalErrorInApp } from '@/shared/utils/show-fatal-error-in-app'
import { tauriFetchStrategy } from '@/shared/utils/tauri'


import { onAuthReadyPlugin } from './app/plugins/auth'
import { dayjsPlugin } from './app/plugins/dayjs'
import { imPlugin } from './app/plugins/im'
import { LinkifyPlugin } from './app/plugins/linkify-jira'
import { localizationPlugin } from './app/plugins/localization'
import { osNotificationPlugin } from './app/plugins/os-notification'
import useSentry from './app/plugins/sentry'
import { YandexMetrikaPlugin } from './app/plugins/yandex-metrika'
import router from './app/router'
import { useStore } from './app/store'
import App from './App.vue'
import './app/assets/tailwind.css'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

const app = createApp(App)

const { i18n } = useLocalization()

app.use(dayjsPlugin)

const appWithPinia = app.use(createPinia())
const appWithInitializedStore = appWithPinia.use(async () => {
  const store = useStore()
  if (tauriMode()) {
    await ensureAppBaseUrlReady()
  }
  try {
    await store.envSettings.loadAllSettings()
  } catch (e) {
    console.error('Failed to load env settings', e)
    // Allow app to mount anyway (e.g. show login or error screen)
  }
})
const appWithSentry = useSentry(appWithInitializedStore, router)

const { localStorageColorTheme } = useColorTheme()

const authOptions: AuthInitOptions = {}
if (tauriMode()) {
  authOptions.optionalDependency = tauriFetchStrategy
  appBaseUrl()
} else if (isElectron()) {
  authOptions.url = appBaseUrl() + '/'
} else {
  // Relative to current origin so authPlugin does not build protocol-relative //api/...
  authOptions.optionalDependency = WuiFetchStrategy
  authOptions.url = '/'
}

useAppBadge().clearAppBadge()

const mountApp = () => {
  app.config.errorHandler = (err, _instance, info) => {
    console.error('Uncaught Vue error:', info, err)
  }

  appWithSentry
    .use(authPlugin, authOptions)
    .use(router)
    .use(imPlugin)
    .use(onAuthReadyPlugin)
    .use(LinkifyPlugin)
    .use(i18n)
    .use(DialogPlugin)
    .use(localizationPlugin)
    .use(YandexMetrikaPlugin, { router })
    .use(osNotificationPlugin)
    .mount('#app')
}

const handleStartupFailure = (err: unknown, phase: 'bootstrapStyles' | 'mountApp') => {
  const message = err instanceof Error ? err.message : String(err)
  console.error(`Application startup failed (${phase})`, err)
  showFatalErrorInApp(message)
}

bootstrapStyles({ theme: localStorageColorTheme, useFonts: ['Inter'] })
  .then(() => {
    clearBootThemeClass()
    try {
      mountApp()
    } catch (err) {
      handleStartupFailure(err, 'mountApp')
    }
  })
  .catch((err: unknown) => {
    handleStartupFailure(err, 'bootstrapStyles')
  })
